import React, { useState, useEffect } from 'react';
import { 
  ShoppingCart, 
  DollarSign, 
  Wrench, 
  CreditCard, 
  Banknote,
  CalendarIcon,
  UserCheck,
  RefreshCw,
  Tag
} from 'lucide-react';
import { format, subDays, startOfMonth, startOfYear } from 'date-fns';
import apiService from '../services/api';
import { DashboardStats, Technician } from '../types';
import toast from 'react-hot-toast';
import { formatCurrency } from '../utils/numberFormat';

const DashboardPage: React.FC = () => {
  // Mock data for development
  const mockStats: DashboardStats = {
    totalOrdersCount: 156,
    totalRevenue: 125000,
    totalServiceCost: 87500,
    onlinePaymentReceived: 75000,
    cashPaymentReceived: 50000
  };

  const mockTechnicians: Technician[] = [
    { 
      id: 1, 
      name: 'John Doe', 
      mobile: '9876543210',
      email: 'john@example.com', 
      status: 'Active',
      specialization: 'Mobile Repair', 
      experience: 5,
      rating: 4.8,
      totalOrders: 150,
      completedOrders: 145,
      averageCompletionTime: 2.5,
      imagePath: '',
      createdAt: new Date(),
      updatedAt: new Date()
    },
    { 
      id: 2, 
      name: 'Jane Smith', 
      mobile: '9876543211',
      email: 'jane@example.com', 
      status: 'Active',
      specialization: 'Laptop Repair', 
      experience: 7,
      rating: 4.9,
      totalOrders: 200,
      completedOrders: 195,
      averageCompletionTime: 3.2,
      imagePath: '',
      createdAt: new Date(),
      updatedAt: new Date()
    },
    { 
      id: 3, 
      name: 'Mike Johnson', 
      mobile: '9876543212',
      email: 'mike@example.com', 
      status: 'Active',
      specialization: 'Tablet Repair', 
      experience: 3,
      rating: 4.6,
      totalOrders: 100,
      completedOrders: 96,
      averageCompletionTime: 2.1,
      imagePath: '',
      createdAt: new Date(),
      updatedAt: new Date()
    },
  ];

  const [stats, setStats] = useState<DashboardStats | null>(mockStats);
  const [loading, setLoading] = useState(false);
  const [technicians, setTechnicians] = useState<Technician[]>(mockTechnicians);
  const [selectedDateRange, setSelectedDateRange] = useState('last7days');
  const [selectedTechnician, setSelectedTechnician] = useState<number | null>(null);
  const [selectedOrderType, setSelectedOrderType] = useState<string | null>(null);
  const [startDate, setStartDate] = useState(format(subDays(new Date(), 7), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));

  useEffect(() => {
    loadTechnicians();
    loadDashboardStats();
  }, []);

  useEffect(() => {
    loadDashboardStats();
  }, [startDate, endDate, selectedTechnician, selectedOrderType]);

  const loadTechnicians = async () => {
    try {
      console.log('Loading technicians...');
      const response = await apiService.getTechnicians();
      if (response.isSuccess) {
        setTechnicians(response.entity);
        console.log('Technicians loaded:', response.entity);
      } else {
        console.warn('API call failed, using mock technicians');
        setTechnicians(mockTechnicians);
      }
    } catch (error) {
      console.error('Error loading technicians:', error);
      console.log('Using mock technicians data');
      setTechnicians(mockTechnicians);
    }
  };

  const loadDashboardStats = async () => {
    setLoading(true);
    try {
      console.log('Loading dashboard stats...');
      const response = await apiService.getDashboardStats(startDate, endDate, selectedTechnician, selectedOrderType);
      if (response.isSuccess) {
        setStats(response.entity);
        console.log('Dashboard stats loaded:', response.entity);
      } else {
        console.warn('API call failed, using mock stats');
        setStats(mockStats);
        toast.error('Using demo data - API not available');
      }
    } catch (error) {
      console.error('Error loading dashboard statistics:', error);
      console.log('Using mock dashboard data');
      setStats(mockStats);
      toast.error('Using demo data - API not available');
    } finally {
      setLoading(false);
    }
  };

  const handleDateRangeChange = (range: string) => {
    setSelectedDateRange(range);
    const today = new Date();
    
    switch (range) {
      case 'last7days':
        setStartDate(format(subDays(today, 7), 'yyyy-MM-dd'));
        setEndDate(format(today, 'yyyy-MM-dd'));
        break;
      case 'last30days':
        setStartDate(format(subDays(today, 30), 'yyyy-MM-dd'));
        setEndDate(format(today, 'yyyy-MM-dd'));
        break;
      case 'thisMonth':
        setStartDate(format(startOfMonth(today), 'yyyy-MM-dd'));
        setEndDate(format(today, 'yyyy-MM-dd'));
        break;
      case 'thisYear':
        setStartDate(format(startOfYear(today), 'yyyy-MM-dd'));
        setEndDate(format(today, 'yyyy-MM-dd'));
        break;
      default:
        break;
    }
  };

  const handleTechnicianChange = (technicianId: string) => {
    setSelectedTechnician(technicianId === 'all' ? null : parseInt(technicianId));
  };

  const handleOrderTypeChange = (orderType: string) => {
    setSelectedOrderType(orderType === 'all' ? null : orderType);
  };

  const statsCards = [
    {
      title: 'Total Orders',
      value: stats?.totalOrdersCount || 0,
      icon: ShoppingCart,
      color: 'text-blue-600',
      bgColor: 'bg-blue-100',
    },
    {
      title: 'Total Revenue',
      value: formatCurrency(stats?.totalRevenue || 0),
      icon: DollarSign,
      color: 'text-green-600',
      bgColor: 'bg-green-100',
    },
    {
      title: 'Total Service Cost',
      value: formatCurrency(stats?.totalServiceCost || 0),
      icon: Wrench,
      color: 'text-orange-600',
      bgColor: 'bg-orange-100',
    },
    {
      title: 'Online Payment Received',
      value: formatCurrency(stats?.onlinePaymentReceived || 0),
      icon: CreditCard,
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-100',
    },
    {
      title: 'Cash Payment Received',
      value: formatCurrency(stats?.cashPaymentReceived || 0),
      icon: Banknote,
      color: 'text-amber-600',
      bgColor: 'bg-amber-100',
    },
  ];

  return (
    <div className="space-y-8">
      {/* Compact Professional Header */}
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between space-y-4 lg:space-y-0">
        <div className="flex-1">
          <div className="flex items-center space-x-3 mb-2">
            <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-primary-600 rounded-lg flex items-center justify-center">
              <ShoppingCart className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">Dashboard</h1>
              <p className="text-sm text-gray-500 mt-0.5">Business performance overview</p>
            </div>
          </div>
        </div>
        
        {/* Compact Filter Controls */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
          <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-4">
            {/* Date Range Filter */}
            <div className="flex flex-col">
              <label className="text-xs font-medium text-gray-600 mb-1.5 flex items-center">
                <CalendarIcon className="h-3.5 w-3.5 mr-1.5 text-gray-400" />
                Date Range
              </label>
              <select
                value={selectedDateRange}
                onChange={(e) => handleDateRangeChange(e.target.value)}
                className="text-sm border border-gray-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent w-40"
              >
                <option value="last7days">Last 7 Days</option>
                <option value="last30days">Last 30 Days</option>
                <option value="thisMonth">This Month</option>
                <option value="thisYear">This Year</option>
                <option value="custom">Custom Range</option>
              </select>
            </div>

            {/* Technician Filter */}
            <div className="flex flex-col">
              <label className="text-xs font-medium text-gray-600 mb-1.5 flex items-center">
                <UserCheck className="h-3.5 w-3.5 mr-1.5 text-gray-400" />
                Technician
              </label>
              <select
                value={selectedTechnician || 'all'}
                onChange={(e) => handleTechnicianChange(e.target.value)}
                className="text-sm border border-gray-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent w-40"
              >
                <option value="all">All Technicians</option>
                {technicians.map((technician) => (
                  <option key={technician.id} value={technician.id.toString()}>
                    {technician.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Order Type Filter */}
            <div className="flex flex-col">
              <label className="text-xs font-medium text-gray-600 mb-1.5 flex items-center">
                <Tag className="h-3.5 w-3.5 mr-1.5 text-gray-400" />
                Order Type
              </label>
              <select
                value={selectedOrderType || 'all'}
                onChange={(e) => handleOrderTypeChange(e.target.value)}
                className="text-sm border border-gray-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent w-40"
              >
                <option value="all">All Types</option>
                <option value="Online">Online</option>
                <option value="Offline">Offline</option>
              </select>
            </div>

            {/* Custom Date Inputs */}
            {selectedDateRange === 'custom' && (
              <>
                <div className="flex flex-col">
                  <label className="text-xs font-medium text-gray-600 mb-1.5">From</label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="text-sm border border-gray-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
                <div className="flex flex-col">
                  <label className="text-xs font-medium text-gray-600 mb-1.5">To</label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="text-sm border border-gray-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
              </>
            )}

            {/* Refresh Button */}
            <div className="flex flex-col justify-end">
              <button
                onClick={loadDashboardStats}
                disabled={loading}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-primary-600 to-primary-700 rounded-lg hover:from-primary-700 hover:to-primary-800 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 transition-all duration-200"
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Professional Stats Grid */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
          {[...Array(5)].map((_, index) => (
            <div key={index} className="bg-white rounded-lg border border-gray-200 p-4 animate-pulse">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gray-200 rounded-lg"></div>
                <div className="flex-1">
                  <div className="h-3 bg-gray-200 rounded w-3/4 mb-2"></div>
                  <div className="h-5 bg-gray-200 rounded w-1/2"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
          {statsCards.map((card, index) => {
            const Icon = card.icon;
            return (
              <div key={index} className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-all duration-200 group cursor-pointer">
                <div className="flex items-center space-x-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${card.bgColor} group-hover:scale-105 transition-transform duration-200`}>
                    <Icon className={`h-5 w-5 ${card.color}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-gray-600 mb-1">{card.title}</p>
                    <p className="text-sm sm:text-base lg:text-lg font-semibold text-gray-900 group-hover:text-primary-700 transition-colors break-words leading-tight">{card.value}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Enhanced Analytics Section */}
      {!loading && stats && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Business Insights Card */}
          <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
            <div className="flex items-center space-x-3 mb-5">
              <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
                <DollarSign className="h-4 w-4 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Business Insights</h3>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between items-center py-2.5 px-3 bg-gray-50 rounded-lg">
                <span className="text-sm font-medium text-gray-700">Total Orders</span>
                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  {stats.totalOrdersCount}
                </span>
              </div>
              <div className="flex justify-between items-center py-2.5 px-3 bg-gray-50 rounded-lg">
                <span className="text-sm font-medium text-gray-700">Average Order Value</span>
                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  {stats && stats.totalOrdersCount > 0 ? 
                    formatCurrency(stats.totalRevenue / stats.totalOrdersCount) : 
                    '₹0'
                  }
                </span>
              </div>
              <div className="flex justify-between items-center py-2.5 px-3 bg-gray-50 rounded-lg">
                <span className="text-sm font-medium text-gray-700">Service Cost Percentage</span>
                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                  {stats && stats.totalRevenue > 0 ? 
                    ((stats.totalServiceCost / stats.totalRevenue) * 100).toFixed(1) : 
                    '0'
                  }%
                </span>
              </div>
            </div>
          </div>

          {/* Payment Analytics Card */}
          <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
            <div className="flex items-center space-x-3 mb-5">
              <div className="w-8 h-8 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-lg flex items-center justify-center">
                <CreditCard className="h-4 w-4 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Payment Analytics</h3>
            </div>
            <div className="space-y-3">
              <div className="p-3 bg-gradient-to-r from-emerald-50 to-emerald-100 rounded-lg border border-emerald-200">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-emerald-800">Online Payments</span>
                  <span className="text-base font-semibold text-emerald-700">
                    {formatCurrency(stats?.onlinePaymentReceived || 0)}
                  </span>
                </div>
              </div>
              <div className="p-3 bg-gradient-to-r from-amber-50 to-amber-100 rounded-lg border border-amber-200">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-amber-800">Cash Payments</span>
                  <span className="text-base font-semibold text-amber-700">
                    {formatCurrency(stats?.cashPaymentReceived || 0)}
                  </span>
                </div>
              </div>
              <div className="p-3 bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg border border-blue-200">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-semibold text-blue-800">Total Received</span>
                  <span className="text-lg font-bold text-blue-700">
                    {formatCurrency((stats?.onlinePaymentReceived || 0) + (stats?.cashPaymentReceived || 0))}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardPage; 